import {
  DurationColumn,
  TaskModel,
  ColumnStore,
} from '@bryntum/gantt/gantt.module';
import styled, { css } from 'styled-components';
import { GanttBarColors } from 'components/colors';
import { Text02 } from 'components/styles';
import '@bryntum/gantt/gantt.classic-light.min.css';
import { BryntumGantt } from './bryntum-gantt-wrapper';
import './overrides';

type GanttProps = {
  splitter?: boolean;
};
type PhaseDependencyProps = {
  canRemoveDependency?: boolean;
};

// Splitter Styles;
const SplitterStyles = css`
  .b-gridbase.b-split .b-grid-splitter {
    display: none;
  }

  .b-grid-footer-scroller,
  .b-grid-header-scroller,
  .b-grid-subgrid.b-grid-horizontal-scroller {
    border-right: solid 1px var(--border-color);
    overflow: hidden;
  }
`;

const RowStyles = css`
  .b-grid-header {
    color: var(--text-color);
  }

  && .b-grid-row {
    border-bottom: 1px solid var(--table-border-color);
    &.b-hover,
    &.b-selected {
      .b-grid-cell,
      .b-grid-cell:hover {
        background: var(--coolest-gray-10);
      }
    }

    &.gantt-private-row {
      &,
      .b-grid-cell {
        background: var(--private-01);
      }

      &.b-hover,
      &:hover {
        &,
        .b-grid-cell {
          background: var(--private-02);
        }
      }
    }

    &.b-selected:not(.b-group-row) {
      background: transparent;
    }

    &.gantt-project_phase .b-tree-cell {
      font-weight: var(--font-weight-bold);
    }
  }
`;
// Grid Styles;
const GridStyles = css`
  .b-grid-header-container {
    height: 41px;
  }

  .b-grid-header-scroller {
    .b-grid-header {
      background: var(--coolest-gray-10);
      font-weight: var(--font-weight-bold);
    }

    &:not(.b-schedulerheader) {
      .b-grid-header {
        padding: 0 var(--layout-01);
      }
    }

    &-text {
      color: var(--text-color);
      font-weight: var(--font-weight-bold);
    }
  }

  .b-sch-header-timeaxis-cell {
    ${Text02};
    font-size: var(--font-size-sm);
  }

  .b-grid-cell {
    color: var(--text-color);
    font-weight: var(--font-weight-normal);
    padding: 0 var(--layout-01);

    &.b-focused:after {
      content: unset;
    }
  }

  ${RowStyles};

  .b-tree-expander {
    width: 24px;
    height: 24px;
    display: inline-flex;
    line-height: 28px;
    margin: -1px var(--spacing-03) 0 0;
  }

  .b-icon-tree-leaf {
    visibility: hidden;
  }

  .b-icon {
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid var(--gray-100);
    display: block;
    min-width: 8px;

    &::before {
      content: unset;
    }

    &-tree-expand {
      transform: rotate(-90deg);
    }
  }
`;

// Gantt bar styles
const GanttBarStyles = GanttBarColors.map(
  (color) =>
    css`
      .gantt-bg-${color} {
        background: var(--${color}-40);

        &:hover {
          background: var(--${color}-50);
        }

        &.gantt-project_phase,
        + .b-sch-label .gantt-type-milestone::before {
          background: var(--${color}-60);

          &:hover {
            background: var(--${color}-70);
          }
        }
      }
    `,
);
// Gantt Styles
const GanttViewStyles = css<PhaseDependencyProps>`
  .b-grid-panel-body,
  .b-gantt-body-wrap {
    height: 100%;
  }
  /* To fix the scroller not taking full height */
  .b-grid-panel-body > .b-grid-body-container > .b-grid-vertical-scroller {
    min-height: 100%;
  }
  .b-sch-foreground-canvas > svg marker {
    fill: var(--gray-30);
  }

  .b-sch-foreground-canvas {
    > svg polyline {
      stroke: var(--gray-30);
    }

    > svg marker path,
    .b-sch-dependency {
      stroke-width: 2 !important;

      &-over {
        ${({ canRemoveDependency }) =>
          canRemoveDependency &&
          `
        stroke-width: 4 !important;
        stroke: var(--red-50) !important;
        cursor: pointer !important;
        `}
      }
    }
  }

  // Making weekend time range transparent by default
  // applying backgrounds only for day and week preset
  // TODO: to find if there is a way to achieve the same via config
  // to avoid rendering weekend time ranges in first place.

  .b-grid-header .b-sch-timerange,
  .b-timeline-subgrid .b-sch-range {
    background-color: transparent;
  }

  &[data-preset-id^='weekAndDay'] {
    .b-grid-header .b-sch-timerange {
      background-color: rgba(255, 215, 217, 0.5);
    }
    .b-timeline-subgrid .b-sch-range {
      background-color: var(--coolest-gray-10);
    }
  }

  .b-sch-terminal {
    border: solid 2px var(--blue-30);
  }

  .b-gantt-task-wrap {
    transition: box-shadow var(--animation-duration-medium) ease-in;

    &:hover {
      .b-sch-event-resizable-true:not(.b-milestone):after {
        content: '';
        width: 4px;
        height: 50%;
        top: 25%;
        position: absolute;
        border-style: solid;
        border-color: #333;
        pointer-events: none;
        z-index: 7;
        margin-right: 3px;
        right: 0;
        border-width: 0 1px;
      }
    }

    &.b-milestone-wrap
      .b-gantt-task:not(.b-gantt-task-withicon)
      .b-gantt-task-content::before {
      height: 12px;
      width: 12px;
    }

    &.b-milestone-wrap {
      margin-left: var(--layout-01);

      .b-gantt-task {
        &,
        &:hover {
          /* background: transparent;
          border: none;
          box-shadow: none; */
        }
      }
    }

    &.b-active::before,
    &.b-active .b-gantt-task {
      outline: 0;
    }

    .b-gantt-task {
      border-radius: var(--border-radius-base);
      box-shadow: var(--shadow-02);
      clip-path: unset;
      transition: background-color var(--animation-duration-medium) ease-in;

      &:hover {
        box-shadow: var(--shadow-16);
      }

      &.gantt-project_phase {
        background-color: var(--blue-60);
        height: 8px;
        max-height: 8px;
        min-height: 8px;

        &:hover {
          background-color: var(--blue-60);
        }
      }

      &.gantt-task {
        height: 24px;
        min-height: 24px;
        max-height: 24px;
      }
    }

    .gantt-type-milestone {
      display: flex;
      align-items: center;
      &::before {
        content: '';
        width: 12px;
        height: 12px;
        transform: translate(0%, 0%) rotate(45deg);
        margin-right: 8px;
      }
    }

    ${GanttBarStyles};
  }

  .b-gantt-task {
    cursor: grab;
  }

  .b-dragging .b-gantt-task {
    cursor: grabbing !important;
  }
`;

// TODO:: Get rid of this when Bryntum does React components on TreeColumn
const StatusStyles = css`
  .b-grid-header[data-column='status'] {
    display: none;
  }
  .b-grid-row [data-column='title'] {
    .b-tree-cell-value {
      padding-left: calc(var(--layout-03) + var(--spacing-03));
    }

    .b-tree-expander + .b-tree-cell-value {
      padding-left: var(--layout-03);
    }
  }

  .b-grid-row {
    [data-column='status'] {
      position: absolute;
      height: 32px;
      top: 4px;
      left: 40px;
      padding: 0;
      display: flex;
      align-items: center;
      text-align: center;
      justify-content: center;
      width: 32px !important;
      min-width: 32px !important;
      > div {
        height: 18px;
      }
    }

    &.gantt-level-1 [data-column='status'] {
      left: 64px;
    }

    &.gantt-level-2 [data-column='status'] {
      left: 88px;
    }

    &.gantt-level-3 [data-column='status'] {
      left: 112px;
    }

    &.gantt-level-4 [data-column='status'] {
      left: 136px;
    }

    &.gantt-level-5 [data-column='status'] {
      left: 156px;
    }
  }
`;

class CustomDurationColumn extends DurationColumn {
  static get $text() {
    return 'CustomDurationColumn';
  }

  static get type() {
    return 'customDuration';
  }

  // eslint-disable-next-line class-methods-use-this
  canEdit(record: TaskModel) {
    return record.manuallyScheduled;
  }
}

ColumnStore.registerColumnType(CustomDurationColumn);

export const Gantt = styled(BryntumGantt).attrs({
  taskMenuFeature: false,
  cellEditFeature: false,
  cellMenuFeature: false,
  columnDragToolbarFeature: false,
  // columnLinesFeature: false,
  columnPickerFeature: false,
  columnReorderFeature: false,
  rowReorderFeature: false,
  taskEditFeature: false,
  eventMenuFeature: false,
  headerMenuFeature: false,
  scheduleMenuFeature: false,
  timeAxisHeaderMenuFeature: false,
  projectLinesFeature: false,
  nonWorkingTimeFeature: true,
  sortFeature: false,
  rowHeight: 40,
  toggleParentTasksOnClick: false,
  snap: true,
  barMargin: 4,
  // columnLines: false,
})<GanttProps & PhaseDependencyProps>`
  height: 100vh;

  ${({ splitter = true }) => !splitter && SplitterStyles};

  ${GridStyles};

  ${GanttViewStyles};

  ${StatusStyles};
`;

export * from './types';
